/**
 * DateInputPtBr - Date input with Brazilian Portuguese locale
 *
 * Uses react-datepicker to display:
 * - Calendar with months in Portuguese (Janeiro, Fevereiro...)
 * - Date format DD/MM/YYYY
 * - Consistent styling with other form inputs
 */

import { forwardRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import { Calendar } from 'lucide-react';
import { cn } from '@/shared/lib/utils';

import 'react-datepicker/dist/react-datepicker.css';

// Register Brazilian Portuguese locale
registerLocale('pt-BR', ptBR);

export interface DateInputPtBrProps {
  /** Current selected date (Date object or null) */
  value: Date | null;
  /** Callback when date changes */
  onChange: (date: Date | null) => void;
  /** Maximum selectable date */
  maxDate?: Date;
  /** Minimum selectable date */
  minDate?: Date;
  /** Additional class names for the container */
  className?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Color variant for styling */
  variant?: 'default' | 'green' | 'blue' | 'amber' | 'purple' | 'rose' | 'teal' | 'cyan' | 'indigo';
  /** Label text */
  label?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Error message */
  error?: string;
}

const variantStyles = {
  default: {
    container: 'border-gray-300 focus-within:ring-amber-500 focus-within:border-amber-500',
    icon: 'text-gray-400',
  },
  green: {
    container: 'border-green-300 focus-within:ring-green-500 focus-within:border-green-500',
    icon: 'text-green-500',
  },
  blue: {
    container: 'border-blue-300 focus-within:ring-blue-500 focus-within:border-blue-500',
    icon: 'text-blue-500',
  },
  amber: {
    container: 'border-amber-300 focus-within:ring-amber-500 focus-within:border-amber-500',
    icon: 'text-amber-500',
  },
  purple: {
    container: 'border-purple-300 focus-within:ring-purple-500 focus-within:border-purple-500',
    icon: 'text-purple-500',
  },
  rose: {
    container: 'border-rose-300 focus-within:ring-rose-500 focus-within:border-rose-500',
    icon: 'text-rose-500',
  },
  teal: {
    container: 'border-teal-300 focus-within:ring-teal-500 focus-within:border-teal-500',
    icon: 'text-teal-500',
  },
  cyan: {
    container: 'border-cyan-300 focus-within:ring-cyan-500 focus-within:border-cyan-500',
    icon: 'text-cyan-500',
  },
  indigo: {
    container: 'border-indigo-300 focus-within:ring-indigo-500 focus-within:border-indigo-500',
    icon: 'text-indigo-500',
  },
};

export const DateInputPtBr = forwardRef<DatePicker, DateInputPtBrProps>(
  (
    {
      value,
      onChange,
      maxDate,
      minDate,
      className,
      disabled = false,
      placeholder = 'dd/mm/aaaa',
      variant = 'default',
      label,
      required,
      error,
    },
    ref
  ) => {
    const styles = variantStyles[variant];
    const hasError = Boolean(error);

    return (
      <div className={cn('w-full', className)}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            {required && <span className="ml-1 text-danger-500">*</span>}
          </label>
        )}
        <div
          className={cn(
            'relative flex items-center w-full px-3 py-2 border rounded-lg bg-white',
            'focus-within:ring-2',
            hasError ? 'border-danger-500 focus-within:ring-danger-500' : styles.container,
            disabled && 'opacity-50 cursor-not-allowed bg-gray-50'
          )}
        >
          <DatePicker
            ref={ref}
            selected={value}
            onChange={onChange}
            maxDate={maxDate}
            minDate={minDate}
            dateFormat="dd/MM/yyyy"
            locale="pt-BR"
            disabled={disabled}
            placeholderText={placeholder}
            showPopperArrow={false}
            popperPlacement="bottom-start"
            className={cn(
              'w-full text-sm font-medium bg-transparent border-none outline-none',
              'focus:ring-0 focus:outline-none',
              disabled && 'cursor-not-allowed'
            )}
            calendarClassName="date-input-ptbr-calendar"
            wrapperClassName="w-full"
          />
          <Calendar className={cn('w-4 h-4 ml-auto flex-shrink-0', hasError ? 'text-danger-500' : styles.icon)} />
        </div>
        {error && (
          <p className="mt-1 text-sm text-danger-500">{error}</p>
        )}
      </div>
    );
  }
);

DateInputPtBr.displayName = 'DateInputPtBr';
